import {isAnyOf} from '@reduxjs/toolkit'

import {initOrderSheetByStorage} from '@web/actions/orderSheetInfo'
import {getAllPointBalance, getChargeableAmount, getChargePointBalance} from '@web/actions/pointPayment'
import {ApiResult, ApiResultType, APIStatus, PayMethod, PayMoneyUserStatus} from '@web/core/constants'
import {getNaverPayPointByApi} from '@web/pageHelpers/ordersheet'
import {PaymoneyDisplayBankNameMap} from '@web/slices/chargePoint/slice'
import makeSlice from '@web/slices/chargePoint/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'

const isLoadingAction = isAnyOf(getAllPointBalance.pending, getChargePointBalance.pending)

const isRejectAction = isAnyOf(getAllPointBalance.rejected, getChargePointBalance.rejected)

function getChargePointStatus(resultType: Maybe<ApiResultType>) {
    return resultType === ApiResult.SUCCESS ? APIStatus.RESOLVED : APIStatus.REJECTED
}

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(initOrderSheetByStorage.fulfilled, (state, {payload}) => {
                const {data} = payload
                state.recharging.amount = data.chargePoint.recharging.amount
            })
            .addCase(initOrderSheetByAPI.fulfilled, (state, {payload}) => {
                const {availablePayMethodTypes} = payload
                const {chargePoint} = payload.naverpayPoint ?? getNaverPayPointByApi()

                const isChargePointAvailable = availablePayMethodTypes.includes(PayMethod.CHARGE_POINT)

                state.amount = isChargePointAvailable ? (chargePoint.chargePointBalance.availableBalance ?? 0) : 0
                state.actualAmount = isChargePointAvailable ? (chargePoint.chargePointBalance.balance ?? 0) : 0

                state.status = getChargePointStatus(chargePoint.chargePointBalance.resultType)
                state.apiResult = chargePoint.chargePointBalance.resultType ?? ApiResult.FAILED_RETRY_AVAILABLE

                state.reloadable.amount = chargePoint.rechargeableChargePoint.reloadableRechargePointAmount ?? 0
                state.reloadable.limitedType = chargePoint.rechargeableChargePoint.rechargeLimitType
                state.reloadable.apiResult =
                    chargePoint.rechargeableChargePoint.resultType ?? ApiResult.FAILED_RETRY_AVAILABLE

                if (chargePoint.chargePointUser.memberType) {
                    state.payMoney.bankCode = chargePoint.chargePointUser.memberType
                    state.payMoney.bankName = PaymoneyDisplayBankNameMap[chargePoint.chargePointUser.memberType]
                }
                state.payMoney.maskedAccount = chargePoint.chargePointUser.maskedPaymoneyBankAccountNo ?? ''
                state.payMoney.userStatus = chargePoint.chargePointUser.userStatus ?? PayMoneyUserStatus.NOT_OPEN
                state.payMoney.apiResult = chargePoint.chargePointUser.resultType ?? ApiResult.FAILED_RETRY_AVAILABLE
            })
            .addCase(getChargeableAmount.fulfilled, (state, {payload}) => {
                state.reloadable.amount = payload.reloadableRechargePointAmount ?? 0
                state.reloadable.limitedType = payload.rechargeLimitType
                state.reloadable.apiResult = payload.resultType ?? ApiResult.FAILED_RETRY_AVAILABLE
            })
            .addCase(getAllPointBalance.fulfilled, (state, {payload}) => {
                const {chargePointBalance} = payload.data
                const status = getChargePointStatus(chargePointBalance.resultType)
                const isSuccess = status === APIStatus.RESOLVED

                state.amount = isSuccess ? (chargePointBalance.availableBalance ?? 0) : 0
                state.actualAmount = isSuccess ? (chargePointBalance.balance ?? 0) : 0
                state.status = status
                state.apiResult = chargePointBalance.resultType ?? ApiResult.FAILED_RETRY_AVAILABLE
            })
            .addCase(getChargePointBalance.fulfilled, (state, {payload}) => {
                const {availableBalance, balance, resultType} = payload.data
                const status = getChargePointStatus(resultType)
                const isSuccess = status === APIStatus.RESOLVED

                state.amount = isSuccess ? (availableBalance ?? 0) : 0
                state.actualAmount = isSuccess ? (balance ?? 0) : 0
                state.status = getChargePointStatus(resultType)
                state.apiResult = resultType ?? ApiResult.FAILED_RETRY_AVAILABLE
            })
            .addMatcher(isLoadingAction, (state) => {
                state.status = APIStatus.FETCHING
            })
            .addMatcher(isRejectAction, (state) => {
                state.amount = 0
                state.actualAmount = 0
                state.status = APIStatus.REJECTED
                state.apiResult = ApiResult.FAILED_RETRY_AVAILABLE
            })
    },
})

export default slice.reducer
